import { useState } from "react";

import { CheckCircleIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";

import Modal from "../../../layouts/Modal";
import PopOutBox from "../../shared/PopOutBox";
import { IOfferUpdateBatchItemStatus, OfferUpdateType } from "../../../models/OfferUpdate";

// Inline status pill for a row's latest eBay offer-update. Failed pills show the error on hover and
// open the full error on click.
const pill =
  "inline-flex items-center justify-center gap-x-1 rounded-full px-2.5 py-1 text-xs font-medium whitespace-nowrap";

// Short label for what the update was, shown on the success pill.
const typeLabel: Record<number, string> = {
  [OfferUpdateType.BestOfferReset]: "Reset",
  [OfferUpdateType.Title]: "Title",
  [OfferUpdateType.Policy]: "Policies",
};

export default function OfferUpdateStatusCell({ status }: { status?: IOfferUpdateBatchItemStatus }) {
  const [modalOpen, setModalOpen] = useState(false);

  const wrap = (child: React.ReactNode) => (
    <div className="flex h-full items-center justify-center">{child}</div>
  );

  if (!status) {
    return wrap(<span className="text-gray-300">—</span>);
  }

  if (status.status === "Pending") {
    return wrap(<span className={`${pill} bg-gray-100 text-gray-600`}>Queued</span>);
  }

  if (status.status === "Processing") {
    return wrap(<span className={`${pill} bg-purple-100 text-purple-700`}>Updating…</span>);
  }

  if (status.status === "Success") {
    const label = typeLabel[status.type] ?? "Updated";
    return wrap(
      <span
        className={`${pill} bg-green-100 text-green-700`}
        title={
          status.type === OfferUpdateType.BestOfferReset && status.newAutoAcceptPrice != null
            ? `New auto-accept £${status.newAutoAcceptPrice.toFixed(2)}`
            : `${label} updated`
        }
      >
        <CheckCircleIcon className="h-3.5 w-3.5" />
        {label}
      </span>
    );
  }

  // Failed
  return wrap(
    <>
      <PopOutBox
        position="left"
        classes="max-w-[40vw] max-h-[40vh] overflow-y-auto"
        icon={
          <span
            className={`${pill} cursor-pointer bg-red-100 text-red-700`}
            onClick={(e) => { e.stopPropagation(); setModalOpen(true); }}
          >
            <ExclamationTriangleIcon className="h-3.5 w-3.5" />
            Failed
          </span>
        }
      >
        <div className="p-2">
          <p className="text-sm font-medium">{typeLabel[status.type] ?? "Update"} failed — click for full error</p>
          <pre className="whitespace-pre-wrap text-xs">{status.errorMessage}</pre>
        </div>
      </PopOutBox>

      <Modal open={modalOpen} setOpen={setModalOpen} width="max-w-[70vw]">
        <div className="p-4">
          <h3 className="mb-4 text-lg font-semibold">eBay Update — Error</h3>
          <div className="max-h-[70vh] overflow-y-auto">
            <pre className="overflow-x-auto whitespace-pre-wrap break-words rounded border bg-gray-50 p-4 text-sm">
              {status.errorMessage}
            </pre>
          </div>
        </div>
      </Modal>
    </>
  );
}
